import { TranslationProvider } from '@infotech-soe/erp-ui-kit';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';

import type { MessageKey } from '../../types';

import ActionProgressContainer from './action-progress/ActionProgressContainer';
import ErrorNotificationContainer from './error-notification/ErrorNotificationContainer';
import IntlProviderContainer from './IntlProviderContainer';
import Main from './Main';
import MainViewContainer from './MainView/MainViewContainer';
import NotFoundView from './NotFoundView/NotFoundView';
import OperationProgressNotificationContainer from './operation-progress/OperationProgressNotificationContainer';
import LoginWrapperContainer from './session/LoginWrapperContainer';
import ThemeProviderContainer from './ThemeProviderContainer';

const NavigationBlocker = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };

    window.history.pushState(null, '', window.location.href);

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  return <>{children}</>;
};

const ReactIntlProvider = ({ children }: { children: React.ReactNode }) => {
  const { formatMessage, formatNumber, locale, messages } = useIntl();
  const t = useCallback(
    (id: string, defaultMessage?: string) =>
      formatMessage({ id: id as MessageKey, defaultMessage }),
    [formatMessage]
  );

  const numberFormatter = useCallback(
    (value: number, options?: Intl.NumberFormatOptions) => {
      const { currencyDisplay, currencySign, useGrouping, ...rest } =
        options ?? {};
      return formatNumber(value, rest);
    },
    [formatNumber]
  );

  return (
    <TranslationProvider
      messages={messages as Record<string, string>}
      locale={locale}
      t={t}
      formatNumber={numberFormatter}
    >
      {children}
    </TranslationProvider>
  );
};

const AppRoot = (): React.JSX.Element => (
  <IntlProviderContainer>
    <ReactIntlProvider>
      <ThemeProviderContainer>
        <BrowserRouter>
          <NavigationBlocker>
            <Main>
              <Routes>
                <Route path="/" element={<LoginWrapperContainer />} />
                <Route path="/app" element={<MainViewContainer />} />
                <Route path="*" element={<NotFoundView />} />
              </Routes>
            </Main>
          </NavigationBlocker>
        </BrowserRouter>
        <OperationProgressNotificationContainer />
        <ActionProgressContainer />
        <ErrorNotificationContainer />
      </ThemeProviderContainer>
    </ReactIntlProvider>
  </IntlProviderContainer>
);

export default AppRoot;
